import type { LookupRead } from '@pedigreesmart/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type SyntheticEvent } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './settings.css'

/**
 * Le liste, con le parole con cui le chiama chi le usa.
 *
 * I codici (`event_type`, `health_type`, …) li decide il backend; qui servono
 * i nomi che una segreteria riconosce, e una riga di spiegazione, perché
 * «qualifica» da solo non dice a cosa serva.
 */
const LISTE: readonly { kind: string; nome: string; spiegazione: string }[] = [
  {
    kind: 'event_type',
    nome: 'Tipi di evento',
    spiegazione: 'Esposizioni, prove di lavoro, controlli funzionali.',
  },
  {
    kind: 'health_type',
    nome: 'Tipi di referto',
    spiegazione: 'Displasie, test genetici, esami che l’associazione riconosce.',
  },
  {
    kind: 'document_type',
    nome: 'Tipi di documento',
    spiegazione: 'Come si classificano i file allegati a soggetti e persone.',
  },
  {
    kind: 'payment_method',
    nome: 'Metodi di pagamento',
    spiegazione: 'Con che cosa si incassano le quote.',
  },
  {
    kind: 'qualification',
    nome: 'Qualifiche',
    spiegazione: 'I titoli attribuiti a una persona: giudice, esperto, allevatore riconosciuto.',
  },
]

/**
 * Il catalogo delle liste configurabili.
 *
 * Serve più di quanto sembri: **senza un tipo a catalogo non si registra un
 * evento**, perché il tipo è un riferimento a questa tabella e non una parola
 * libera. Finché questa schermata non è esistita, l'unico modo di aggiungerne
 * uno era una richiesta a mano.
 *
 * **I valori di sistema si vedono e non si toccano.** La risposta non dice
 * quali lo siano — il tenant non compare — quindi qui non si indovina: si
 * lascia provare e si riporta il rifiuto del server. Nascondere righe in base
 * a una supposizione sarebbe peggio, perché una supposizione sbagliata
 * toglierebbe di vista dati veri.
 */
export function LookupList() {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [lista, setLista] = useState(LISTE[0]?.kind ?? 'event_type')
  const [errore, setErrore] = useState<string | null>(null)

  const valori = useQuery({
    queryKey: ['lookups', lista],
    queryFn: ({ signal }) => resources.lookups.list(lista, signal),
  })

  const crea = useMutation({
    mutationFn: (corpo: { kind: string; label: string; code?: string }) =>
      resources.lookups.create(corpo),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['lookups', lista] })
    },
    onError: (e: unknown) => {
      const problema = e as {
        status?: number
        violations?: readonly { message: string }[]
      }

      setErrore(
        problema.violations?.[0]?.message ??
          (problema.status === 403
            ? 'Il vostro profilo non può modificare le liste.'
            : 'Non è stato possibile aggiungere il valore.'),
      )
    },
  })

  const scelta = LISTE.find((l) => l.kind === lista)

  function aggiungi(evento: SyntheticEvent<HTMLFormElement>) {
    evento.preventDefault()

    const campi = new FormData(evento.currentTarget)
    const etichetta = ((campi.get('label') as string | null) ?? '').trim()
    const codice = ((campi.get('code') as string | null) ?? '').trim()

    if (etichetta === '') return

    crea.mutate({ kind: lista, label: etichetta, ...(codice === '' ? {} : { code: codice }) })
    evento.currentTarget.reset()
  }

  return (
    <section aria-labelledby="titolo-liste">
      <div className="elenco__testa">
        <h1 id="titolo-liste">Liste</h1>
      </div>

      <label className="elenco__ricerca">
        <span>Quale lista</span>
        <select
          value={lista}
          onChange={(evento) => {
            setLista(evento.target.value)
            setErrore(null)
          }}
        >
          {LISTE.map((l) => (
            <option key={l.kind} value={l.kind}>
              {l.nome}
            </option>
          ))}
        </select>
      </label>

      {scelta && <p className="messaggio">{scelta.spiegazione}</p>}

      {errore && (
        <p role="alert" className="messaggio messaggio--errore">
          {errore}
        </p>
      )}

      {valori.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere la lista.
        </p>
      )}

      {valori.data && valori.data.member.length > 0 && (
        <div className="tabella-scorrevole">
          <table>
            <thead>
              <tr>
                <th scope="col">Etichetta</th>
                <th scope="col">Codice</th>
                <th scope="col">In uso</th>
              </tr>
            </thead>
            <tbody>
              {valori.data.member.map((valore) => (
                <Riga key={valore.id} valore={valore} lista={lista} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {valori.data?.member.length === 0 && (
        <p className="messaggio">
          Questa lista è vuota. Finché lo resta, i campi che la usano non hanno valori da offrire.
        </p>
      )}

      <form className="modulo modulo--breve" onSubmit={aggiungi}>
        <fieldset>
          <legend>Aggiungi un valore</legend>
          <label className="modulo__campo">
            <span>
              Etichetta <abbr title="obbligatorio">*</abbr>
            </span>
            <input type="text" name="label" placeholder="Esposizione nazionale" />
          </label>
          <label className="modulo__campo">
            <span>Codice</span>
            {/* Facoltativo: serve a chi legge l'API, non a chi compila. Un
                codice assente non impedisce niente. */}
            <input type="text" name="code" placeholder="facoltativo" />
          </label>
          <div className="modulo__azioni">
            <button type="submit" disabled={crea.isPending}>
              {crea.isPending ? 'Aggiunta…' : 'Aggiungi'}
            </button>
          </div>
        </fieldset>
      </form>
    </section>
  )
}

function Riga({ valore, lista }: { valore: LookupRead; lista: string }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)

  const salva = useMutation({
    mutationFn: (modifica: { active?: boolean; label?: string }) =>
      resources.lookups.update(valore.id, modifica),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['lookups', lista] })
    },
    onError: (e: unknown) => {
      setErrore(
        (e as { status?: number }).status === 403
          ? 'È un valore di sistema: si usa ma non si modifica.'
          : 'Non è stato possibile salvare.',
      )
    },
  })

  return (
    <tr>
      <td>
        <span className="riga__nome">{valore.label}</span>
        {errore && (
          <span className="modulo__errore" role="alert">
            {errore}
          </span>
        )}
      </td>
      <td className="codice">{valore.code ?? '—'}</td>
      <td>
        <label className="persone__interruttore">
          <input
            type="checkbox"
            checked={valore.active}
            disabled={salva.isPending}
            onChange={(evento) => {
              salva.mutate({ active: evento.target.checked })
            }}
          />
          {valore.active ? 'Sì' : 'No'}
        </label>
      </td>
    </tr>
  )
}
